import { appRoles } from "@/src/constants/roles-permissions.constants";
import { BlocksUser } from "@/src/models/user.model";
import { formatRoleLabel, getPrimaryRole } from "@/src/utils/role.utils";
import { capitalize } from "@/src/utils/string.utils";
import { Pressable, Text, View } from "react-native";
import BlocksAvatar from "../../ui-kit/blocks-avatar";
import { HoverTooltip } from "../../ui-kit/hover-tooltip";

const getRoleBadgeStyle = (role: string) => {
  // Normalise the separator: prod's slug is `super_admin`, dev's `super-admin`.
  switch (role.toLowerCase().replace(/_/g, "-")) {
    case "super-admin":
    case "admin":
      return { bg: "bg-primary", text: "text-white" };
    case "member":
      return { bg: "bg-primary-1", text: "text-primary" };
    default:
      return { bg: "bg-ash", text: "text-slate" };
  }
};

type RoleBadgeCellProps = Readonly<{
  roles: string[];
}>;

export function RoleBadgeCell({ roles }: RoleBadgeCellProps) {
  // `clouduser` is platform access, not an app role — it must not appear in the
  // badge, the tooltip, or the "+N" count. The tooltip listed the raw array, so
  // it leaked there even though the badge itself already filtered it.
  const displayRoles = appRoles(roles);
  const primaryRole = getPrimaryRole(roles);
  const extraCount = Math.max(0, displayRoles.length - 1);
  const { bg, text } = getRoleBadgeStyle(primaryRole);

  return (
    <HoverTooltip
      disabled={displayRoles.length <= 1}
      unstyled
      content={
        <View className="bg-chalk border border-ash-3 rounded-md p-2 flex-row flex-wrap gap-1 max-w-[220px]">
          {displayRoles.map((role) => (
            <View key={role} className="bg-ash-3 rounded-full px-2 py-0.5">
              <Text className="text-onyx text-xs">{formatRoleLabel(role)}</Text>
            </View>
          ))}
        </View>
      }
    >
      <View className="flex-row items-center gap-1 self-start">
        <View className={`${bg} px-2.5 py-0.5 rounded-full`}>
          <Text className={`${text} text-xs font-medium`}>
            {formatRoleLabel(primaryRole)}
          </Text>
        </View>
        {extraCount > 0 && (
          <View className="bg-ash px-1.5 py-0.5 rounded-full">
            <Text className="text-slate text-xs font-medium">
              +{extraCount}
            </Text>
          </View>
        )}
      </View>
    </HoverTooltip>
  );
}

type UserNameCellProps = Readonly<{
  blocksUser: BlocksUser;
  onPress: (blocksUser: BlocksUser) => void;
}>;

export function UserNameCell({ blocksUser, onPress }: UserNameCellProps) {
  const fullName = `${capitalize(blocksUser?.firstName)} ${capitalize(blocksUser?.lastName)}`;

  return (
    <Pressable
      className="flex-row items-center gap-2 flex-1 hover:bg-ash-3 hover:rounded-lg"
      onPress={() => onPress(blocksUser)}
    >
      {/* No `userId` — the row already carries this user's data from the bulk
          list fetch, so this reuses BlocksAvatar's broken-image/initials
          fallback without an extra per-row lookup. */}
      <BlocksAvatar
        fallbackImageUrl={blocksUser?.profileImageUrl}
        firstName={blocksUser?.firstName}
        lastName={blocksUser?.lastName}
        size={32}
      />
      <View className="flex-1 min-w-0">
        <HoverTooltip
          className="min-w-0"
          content={<Text className="text-chalk text-xs">{fullName}</Text>}
        >
          <Text className="text-slate" numberOfLines={1} ellipsizeMode="tail">
            {fullName}
          </Text>
        </HoverTooltip>
      </View>
    </Pressable>
  );
}

type UserEmailCellProps = Readonly<{
  email?: string;
}>;

export function UserEmailCell({ email }: UserEmailCellProps) {
  return (
    <HoverTooltip
      className="flex-1"
      disabled={!email}
      content={<Text className="text-chalk text-xs">{email}</Text>}
    >
      <Text className="text-slate" numberOfLines={1} ellipsizeMode="tail">
        {email}
      </Text>
    </HoverTooltip>
  );
}
